import { createTheme } from '@mui/material/styles'
import { CSSProperties } from 'react'

declare module '@mui/material/styles' {
  interface TypographyVariants {
    poster: CSSProperties
    heroTitle: CSSProperties
    alertTitle: CSSProperties
    alertDescription: CSSProperties
    avatarInitials_lg: CSSProperties
    avatarInitials_small: CSSProperties
    badgeLabel: CSSProperties
    bottomNavActiveLabel: CSSProperties
    buttonLarge_lg: CSSProperties
    buttonLarge_md: CSSProperties
    buttonLarge_sm: CSSProperties
    buttonLink: CSSProperties
    chipLabel: CSSProperties
    chipLabelBold: CSSProperties
    subtitle3: CSSProperties
    calendarPicker: CSSProperties
    inputLabel: CSSProperties
    inputValue: CSSProperties
    inputHelper: CSSProperties
    listSubheader: CSSProperties
    menuItemDefault: CSSProperties
    menuItemDense: CSSProperties
    tableHeader: CSSProperties
    tableHeaderBoard: CSSProperties
    tooltipLabel: CSSProperties
    dataGrid: CSSProperties
    fiftyPx: CSSProperties
    xxl: CSSProperties
    helper: CSSProperties
    caption1: CSSProperties
    tableCell: CSSProperties
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    poster?: CSSProperties
    heroTitle?: CSSProperties
    alertTitle?: CSSProperties
    alertDescription?: CSSProperties
    avatarInitials_lg?: CSSProperties
    avatarInitials_small?: CSSProperties
    badgeLabel?: CSSProperties
    bottomNavActiveLabel?: CSSProperties
    buttonLarge_lg?: CSSProperties
    buttonLarge_md?: CSSProperties
    buttonLarge_sm?: CSSProperties
    buttonLink?: CSSProperties
    chipLabel?: CSSProperties
    chipLabelBold?: CSSProperties
    subtitle3?: CSSProperties
    calendarPicker?: CSSProperties
    inputLabel?: CSSProperties
    inputValue?: CSSProperties
    inputHelper?: CSSProperties
    listSubheader?: CSSProperties
    menuItemDefault: CSSProperties
    menuItemDense: CSSProperties
    tableHeader: CSSProperties
    tableHeaderBoard: CSSProperties
    tooltipLabel: CSSProperties
    dataGrid: CSSProperties
    fiftyPx: CSSProperties
    xxl: CSSProperties
    helper: CSSProperties
    caption1: CSSProperties
    tableCell: CSSProperties
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    poster: true
    heroTitle: true
    alertTitle: true
    alertDescription: true
    avatarInitials_lg: true
    avatarInitials_small: true
    badgeLabel: true
    bottomNavActiveLabel: true
    buttonLarge_lg: true
    buttonLarge_md: true
    buttonLarge_sm: true
    buttonLink: true
    chipLabel: true
    chipLabelBold: true
    subtitle3: true
    calendarPicker: true
    inputLabel: true
    inputValue: true
    inputHelper: true
    listSubheader: true
    menuItemDefault: true
    menuItemDense: true
    tableHeader: true
    tableHeaderBoard: true
    tooltipLabel: true
    dataGrid: true
    fiftyPx: true
    xxl: true
    helper: true
    caption1: true
    tableCell: true
  }
}

export const theme = createTheme({
  palette: {
    primary: {
      main: '#26bbed',
      dark: '#1501B0',
      light: '#5C48F5'
    },
    text: {
      primary: '#000000DE',
      secondary: '#00000099',
      disabled: '#00000061'
    },
    secondary: {
      main: '#26bbed',
      dark: '#7B1FA2',
      light: '#BA68C8'
    },
    error: {
      main: '#D32F2F',
      dark: '#C62828',
      light: '#EF5350'
    },
    warning: {
      main: '#EF6C00',
      dark: '#EF6C00',
      light: '#FF9800'
    },
    info: {
      main: '#0288D1',
      dark: '#01579B',
      light: '#03A9F4'
    },
    success: {
      main: '#2E7D32',
      dark: '#1B5E20',
      light: '#4CAF50'
    },
    action: {
      active: '#0000008F',
      hover: '#0000000A',
      selected: '#46586B14', // "#00000014",
      disabledBackground: '#0000001F',
      focus: '#4729FF80', //button outlined border
      disabled: '#0000004D'
      // labelBackground: "#46586B14",
    },
    common: {
      white: '#FFFFFF',
      black: '#000000DE'
    }
  },

  typography: {
    allVariants: {
      fontWeight: 400,
      fontFamily: '"Inter", sans-serif',
      color: '#000000DE'
    },
    xxl: {
      fontSize: 42,
      lineHeight: '49.01px',
      fontWeight: 600,
      letterSpacing: -1.6
    },
    h1: {
      fontSize: 32,
      lineHeight: '37.344px',
      fontWeight: 600,
      letterSpacing: -1.6
    },
    h2: {
      fontSize: 29,
      lineHeight: '34.8px',
      letterSpacing: -1.45
    },
    h3: {
      fontSize: 40,
      lineHeight: '46.80px',
      letterSpacing: -2
    },
    h4: {
      fontSize: 32,
      lineHeight: '39.68px',
      fontWeight: 600,
      letterSpacing: -1.6
    },
    h5: {
      fontSize: 25,
      lineHeight: '33.25px',
      letterSpacing: -1.25
    },
    h6: {
      fontSize: 20,
      lineHeight: '25.20px',
      fontWeight: 400,
      letterSpacing: -1
    },
    subtitle1: {
      fontSize: 16,
      lineHeight: '20.80px',
      fontWeight: 600,
      letterSpacing: -0.02
    },
    subtitle2: {
      fontSize: 14,
      lineHeight: '21.98px',
      fontWeight: 600,
      letterSpacing: -0.05
    },
    subtitle3: {
      fontSize: 14,
      lineHeight: '21.98px',
      fontWeight: 700,
      letterSpacing: -0.7
    },
    helper: {
      fontSize: 12,
      lineHeight: '19.92px',
      fontWeight: 400,
      letterSpacing: -0.3
    },
    body1: {
      fontSize: 16,
      lineHeight: '24px',
      letterSpacing: -0.48
    },
    body2: {
      fontSize: 14,
      lineHeight: '19.60px',
      letterSpacing: -0.03
    },
    overline: {
      fontSize: 12,
      lineHeight: '19.2px',
      letterSpacing: 0
    },
    caption: {
      fontSize: 12,
      lineHeight: '19.92px',
      letterSpacing: -0.36,
      fontWeight: 400
    },
    caption1: {
      fontSize: 12,
      lineHeight: '19.92px',
      letterSpacing: -0.36,
      fontWeight: 400
    },
    alertTitle: {
      fontSize: 16,
      lineHeight: '24px',
      letterSpacing: -0.48,
      fontWeight: 500
    },
    alertDescription: {
      fontSize: 14,
      lineHeight: '20.02px',
      letterSpacing: -0.42,
      fontWeight: 500
    },
    avatarInitials_lg: {
      fontSize: 18,
      fontWeight: 500,
      lineHeight: '20px',
      letterSpacing: -0.6
    },
    avatarInitials_small: {
      fontWeight: 500,
      fontSize: 16,
      letterSpacing: -0.36,
      lineHeight: '18px'
    },
    badgeLabel: {
      fontWeight: 500,
      fontSize: 12,
      lineHeight: '20px',
      letterSpacing: -0.03
    },
    bottomNavActiveLabel: {
      fontSize: 14,
      lineHeight: '23.24px',
      letterSpacing: -0.45
    },
    buttonLarge_lg: {
      fontWeight: 600,
      fontSize: 15,
      lineHeight: '21px',
      letterSpacing: -0.03
    },
    buttonLarge_md: {
      fontWeight: 600,
      fontSize: 14,
      lineHeight: '19.32px',
      letterSpacing: -0.03
    },
    buttonLarge_sm: {
      fontWeight: 600,
      fontSize: 13,
      lineHeight: '18.2px',
      letterSpacing: -0.42
    },
    buttonLink: {
      fontWeight: 600,
      fontSize: 14,
      lineHeight: '19.6px',
      letterSpacing: -0.39
    },
    chipLabel: {
      fontWeight: 500,
      fontSize: 13,
      lineHeight: '18px',
      letterSpacing: -0.42
    },
    chipLabelBold: {
      fontWeight: 500,
      fontSize: 13,
      lineHeight: '18px',
      letterSpacing: -0.42
    },
    calendarPicker: {
      fontWeight: 500,
      fontSize: 16,
      lineHeight: '24px',
      letterSpacing: -0.39
    },
    inputLabel: {
      fontSize: 12,
      lineHeight: '12px',
      letterSpacing: -0.48
    },
    inputValue: {
      fontSize: 16,
      lineHeight: '22.4px',
      letterSpacing: -0.36
    },
    inputHelper: {
      fontSize: 12,
      lineHeight: '19.92px',
      letterSpacing: -0.48
    },
    tableCell: {
      fontSize: 13,
      fontWeight: 400,
      lineHeight: '100%',
      letterSpacing: 0
    },
    listSubheader: {
      fontSize: 14,
      fontWeight: 500,
      lineHeight: '48px',
      letterSpacing: -0.36
    },
    menuItemDefault: {
      fontSize: 16,
      lineHeight: '22.4px',
      letterSpacing: -0.03
    },
    menuItemDense: {
      fontSize: 14,
      lineHeight: '19.6px',
      letterSpacing: -0.48
    },
    tableHeader: {
      fontWeight: 600,
      fontSize: 14,
      lineHeight: '16px',
      letterSpacing: -0.42
    },
    tableHeaderBoard: {
      fontWeight: 500,
      fontSize: 13,
      lineHeight: '16px',
      letterSpacing: -0.2
    },
    tooltipLabel: {
      fontWeight: 500,
      fontSize: 10,
      lineHeight: '14px',
      letterSpacing: -0.42
    },
    dataGrid: {
      fontWeight: 500,
      fontSize: 12,
      lineHeight: '12px',
      letterSpacing: -0.3
    },
    fiftyPx: {
      fontWeight: 400,
      fontSize: 50,
      lineHeight: '36px',
      letterSpacing: -1.5
    }
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(68, 68, 68, 0.96)', // #444444 @ 96%
          color: '#fff',
          fontSize: '0.875rem',
          fontWeight: 400,
          maxWidth: '240px',
          borderRadius: '6px',
          padding: '8px 12px',
          whiteSpace: 'normal',
          wordBreak: 'break-word'
        },
        arrow: {
          color: 'rgba(68, 68, 68, 0.96)'
        }
      }
    }
  }
})
